/**
 * Smoothly animates a numeric value from 0 (or the previous value) to
 * `value` over 400ms using requestAnimationFrame with ease-out cubic.
 *
 * Pure CSS transitions can't tween text content, so this is the lightest
 * way to get a counter animation without bringing in framer-motion.
 * Pauses naturally when the tab is hidden (rAF doesn't fire).
 */
import React, { useEffect, useRef, useState } from "react";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export function AnimatedCounter({ value, duration = 400 }) {
    const [display, setDisplay] = useState(value);
    const fromRef = useRef(value);
    const rafRef = useRef(null);

    useEffect(() => {
        const from = fromRef.current ?? 0;
        const to = Number(value) || 0;
        if (from === to) {
            setDisplay(to);
            return;
        }
        const start = performance.now();
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = easeOutCubic(t);
            const next = Math.round(from + (to - from) * eased);
            setDisplay(next);
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                fromRef.current = to;
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [value, duration]);

    return <>{display.toLocaleString()}</>;
}

export default AnimatedCounter;
